// ============================================================================
// CloseTaskCard — editable proposal card for propose_close_task.
//
// One card per (facility, batch). Lists every FTK that would be touched,
// with per-row checkboxes (default checked). The AM can:
//   - deselect rows that shouldn't actually be closed
//   - edit the completion_notes
// new_status is locked to 'Done' (the only close target for tasks).
// "Do it →" writes per-row UPDATEs in a single client-side batch under RLS.
// ============================================================================

import { useMemo, useState } from 'react';
import { CheckSquare } from 'lucide-react';
import type { CopilotProposalBase } from '../../../shared/types';
import { CardShell } from './card-shell';

interface FacilityOpt {
  facility_id: string;
  facility_name: string;
}

interface TaskRow {
  task_id: string;
  description?: string | null;
  priority?: string | null;
  due_date?: string | null;
  status?: string | null;
}

interface CloseTaskCardProps {
  proposal: CopilotProposalBase;
  facilities: FacilityOpt[];
  isCommitting: boolean;
  onDoIt: (
    edited_input: Record<string, unknown>,
    was_edited: boolean,
  ) => void;
  onDiscard: () => void;
}

export function CloseTaskCard({
  proposal,
  facilities,
  isCommitting,
  onDoIt,
  onDiscard,
}: CloseTaskCardProps) {
  const initial = proposal.input as {
    facility_id?: string;
    task_ids?: string[] | null;
    completion_notes?: string;
    rows?: TaskRow[];
    /** Open items here this person may not close — left off the card (Ask AI, decision 5 A). */
    left_off?: number;
  };

  const expandedRows: TaskRow[] = useMemo(
    () => (Array.isArray(initial.rows) ? initial.rows : []),
    [initial.rows],
  );

  const initialIds: string[] = useMemo(
    () =>
      Array.isArray(initial.task_ids)
        ? initial.task_ids
        : expandedRows.map((r) => r.task_id),
    [initial.task_ids, expandedRows],
  );

  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(initialIds),
  );
  const [completionNotes, setCompletionNotes] = useState(
    initial.completion_notes ?? '',
  );

  const facilityName = useMemo(
    () =>
      facilities.find((f) => f.facility_id === initial.facility_id)
        ?.facility_name ?? initial.facility_id ?? '',
    [facilities, initial.facility_id],
  );

  const wasEdited = useMemo(() => {
    if (completionNotes !== (initial.completion_notes ?? '')) return true;
    const initialSet = new Set(initialIds);
    if (initialSet.size !== selected.size) return true;
    for (const id of selected) if (!initialSet.has(id)) return true;
    for (const id of initialSet) if (!selected.has(id)) return true;
    return false;
  }, [completionNotes, selected, initial, initialIds]);

  const toggleRow = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleDoIt = () => {
    onDoIt(
      {
        facility_id: initial.facility_id,
        task_ids: Array.from(selected),
        completion_notes: completionNotes.trim(),
        rows: expandedRows,
      },
      wasEdited,
    );
  };

  const selectedCount = selected.size;
  const leftOff = typeof initial.left_off === 'number' ? initial.left_off : 0;
  const canSubmit =
    selectedCount > 0 && completionNotes.trim().length > 0 && !isCommitting;

  return (
    <CardShell
      title={`Close tasks at ${facilityName}`}
      icon={<CheckSquare className="h-4 w-4" />}
      accentClass="border-st-info-border-strong"
      status={proposal.status}
      committedLabel={
        proposal.result && proposal.result.done < proposal.result.of
          ? `Marked ${proposal.result.done} of ${proposal.result.of} Done — the rest were not yours to close`
          : (proposal.result?.done ?? selectedCount) === 1
          ? `Marked 1 task Done`
          : `Marked ${proposal.result?.done ?? selectedCount} tasks Done`
      }
      isCommitting={isCommitting}
      onDoIt={canSubmit ? handleDoIt : () => {}}
      onDiscard={onDiscard}
    >
      {leftOff > 0 && (
        <p className="rounded-md bg-st-warn-bg px-2 py-1.5 text-xs text-st-warn-fg-strong">
          {leftOff} more open {leftOff === 1 ? 'item here is' : 'items here are'} not yours to close, so{' '}
          {leftOff === 1 ? 'it is' : 'they are'} not on this card. The building&apos;s account manager can close{' '}
          {leftOff === 1 ? 'it' : 'them'}.
        </p>
      )}
      <Field label={`Marking ${selectedCount} task${selectedCount === 1 ? '' : 's'} Done`}>
        <div className="text-xs text-tcs-text-muted">
          Tasks transition to <span className="font-medium">Done</span>. Use
          completion notes to capture what was accomplished.
        </div>
      </Field>

      <Field label="Completion notes">
        <textarea
          value={completionNotes}
          onChange={(e) => setCompletionNotes(e.target.value)}
          rows={2}
          placeholder="Short narrative of how the task was completed."
          className={`${inputClass} resize-none`}
        />
      </Field>

      <Field label="Items">
        <ul className="space-y-1.5 rounded-md border border-tcs-border bg-tcs-bg-muted p-2 max-h-56 overflow-y-auto">
          {expandedRows.length === 0 && (
            <li className="text-xs text-tcs-text-muted italic">No rows attached.</li>
          )}
          {expandedRows.map((row) => {
            const checked = selected.has(row.task_id);
            const excerpt =
              typeof row.description === 'string' && row.description.length > 100
                ? row.description.slice(0, 97) + '...'
                : row.description ?? '';
            const priorityClass =
              row.priority === 'High'
                ? 'bg-st-danger-bg-strong text-st-danger-fg-strong'
                : row.priority === 'Low'
                ? 'bg-tcs-surface-strong text-tcs-text-secondary'
                : 'bg-st-info-bg-strong text-st-info-fg-strong';
            return (
              <li key={row.task_id}>
                <label className="flex gap-2 items-start cursor-pointer">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleRow(row.task_id)}
                    className="mt-1 h-4 w-4 rounded border-tcs-border-strong text-tcs-text focus:ring-tcs-text-muted"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 text-xs">
                      <span className="font-mono text-tcs-text-secondary">
                        {row.task_id}
                      </span>
                      {row.priority && (
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${priorityClass}`}>
                          {row.priority}
                        </span>
                      )}
                      {row.due_date && (
                        <span className="text-tcs-text-muted">due {row.due_date}</span>
                      )}
                    </div>
                    <div className="text-xs text-tcs-text-secondary mt-0.5">{excerpt}</div>
                  </div>
                </label>
              </li>
            );
          })}
        </ul>
      </Field>
    </CardShell>
  );
}

const inputClass =
  'w-full rounded-md border border-tcs-border bg-tcs-field px-2 py-1.5 text-sm text-tcs-text focus:border-tcs-text-muted focus:outline-none focus:ring-1 focus:ring-tcs-text-muted';

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-tcs-text-muted">
        {label}
      </span>
      {children}
    </label>
  );
}
